import React, { useState, useCallback, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useAuth } from "../Context/AuthContext";
import { classNames } from "../utils/helpers";

// Estilos mejorados
const SidebarLink = styled(NavLink)`
  display: flex;
  color: #969696;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  margin: 8px 15px;
  list-style: none;
  height: 40px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  border-radius: 5px;
  transition: all 0.2s ease;
  cursor: ${(props) => (props.$hasSubmenu ? "pointer" : "pointer")};

  &:hover {
    background-color: #eeeeee;
    color: #969696;
  }

  &.active {
    color: rgb(0, 156, 255);
    background-color: #f0f0f0;
    border-right: 5px solid #009cff;
    transition: all 0.2s ease;
  }
`;

const SidebarLabel = styled.span`
  margin-left: 5px;
`;

// Contenedor para elementos sin path
const NonLinkContainer = styled.div`
  display: flex;
  color: #969696;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  margin: 8px 15px;
  user-select: none;
  height: 40px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 5px;
  cursor: ${(props) => (props.$hasSubmenu ? "pointer" : "default")};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${(props) =>
      props.$hasSubmenu ? "#eeeeee" : "transparent"};
  }
`;

// Contenedor principal del menú que asegura el flujo correcto
const MenuItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// Contenedor para los submenús que empuja el contenido
const SubMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`;

// Versión simplificada del DropdownLink para submenús
const SubMenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 10px 15px 10px 45px;
  margin: 2px 15px;
  text-decoration: none;
  color: #969696;
  font-size: 13px;
  font-weight: 500;
  border-radius: 5px;
  transition: all 0.2s ease;
  background-color: #f5f5f5;

  &:hover {
    background-color: #f5f5f5;
    color: rgb(0, 156, 255);
  }

  &.active {
    color: rgb(0, 156, 255);
    background-color: #f0f0f0;
    border-right: 5px solid #009cff;
  }
`;

const SubMenu = ({ item, showSidebar }) => {
  const location = useLocation();
  const [subnav, setSubnav] = useState(false);
  const auth = useAuth();
  const channels = auth.getUserChannel();

  const hasSubmenu = item.subNav && item.subNav.length > 0;

  // Verificar si el item actual o algún subitem está activo
  const isActive = useCallback(() => {
    if (item.path && location.pathname === item.path) return true;
    if (hasSubmenu) {
      return item.subNav.some(
        (subItem) => subItem.path && location.pathname === subItem.path,
      );
    }
    return false;
  }, [location.pathname, item.path, hasSubmenu, item.subNav]);

  // Auto-expandir si un hijo está activo
  useEffect(() => {
    if (hasSubmenu && !subnav) {
      const hasActiveChild = item.subNav.some(
        (subItem) => subItem.path && location.pathname === subItem.path,
      );
      if (hasActiveChild) {
        setSubnav(true);
      }
    }
  }, [location.pathname, hasSubmenu, item.subNav]);

  const showSubnav = useCallback(() => {
    if (hasSubmenu) {
      setSubnav((prev) => !prev);
    }
  }, [hasSubmenu]);

  const handleItemClick = useCallback(() => {
    if (hasSubmenu) {
      showSubnav();
    } else if (showSidebar && item.path) {
      showSidebar();
    }
  }, [hasSubmenu, showSubnav, showSidebar, item.path]);

  const active = isActive();

  // Filtrar subitems por canales
  const filteredSubNav = hasSubmenu
    ? item.subNav.filter(
        (subItem) =>
          !subItem.channel ||
          !channels ||
          channels.includes(subItem.channel) ||
          channels.includes("ALL"),
      )
    : [];

  return (
    <MenuItemContainer>
      {item.path ? (
        <SidebarLink
          to={item.path}
          onClick={handleItemClick}
          className={classNames({ active })}
          $hasSubmenu={hasSubmenu}
        >
          <div>
            {item.icon}
            <SidebarLabel>{item.title}</SidebarLabel>
          </div>
          {hasSubmenu && (
            <div
              style={{
                transform: subnav ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              {subnav ? item.iconOpened || "▼" : item.iconClosed || "▶"}
            </div>
          )}
        </SidebarLink>
      ) : (
        <NonLinkContainer onClick={handleItemClick} $hasSubmenu={hasSubmenu}>
          <div>
            {item.icon}
            <SidebarLabel>{item.title}</SidebarLabel>
          </div>
          {hasSubmenu && (
            <div
              style={{
                transform: subnav ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease",
              }}
            >
              {subnav ? item.iconOpened || "▼" : item.iconClosed || "▶"}
            </div>
          )}
        </NonLinkContainer>
      )}

      {/* Submenús que empujan el contenido hacia abajo */}
      {hasSubmenu && (
        <SubMenuContainer>
          {subnav &&
            filteredSubNav.map((subItem, index) => (
              <SubMenuItem
                to={subItem.path}
                key={index}
                onClick={showSidebar}
                className={classNames({
                  active: location.pathname === subItem.path,
                })}
              >
                {subItem.icon && (
                  <span style={{ marginRight: "8px" }}>{subItem.icon}</span>
                )}
                <SidebarLabel>{subItem.title}</SidebarLabel>
              </SubMenuItem>
            ))}
        </SubMenuContainer>
      )}
    </MenuItemContainer>
  );
};

SubMenu.propTypes = {
  item: PropTypes.shape({
    path: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.node,
    iconOpened: PropTypes.node,
    iconClosed: PropTypes.node,
    subNav: PropTypes.array,
  }),
  showSidebar: PropTypes.func,
};

export default SubMenu;
